import React, { useEffect, useState } from 'react';
import { Calendar, Users, BedDouble, Activity, BarChart3, Building2, Plus, Search } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import DashboardLayout from '../../components/common/DashboardLayout';
import { Card, LoadingState, StatusBadge, Badge, Button, Modal, Input, Select, Avatar, DoshaTag, EmptyState } from '../../components/common/UI';
import { appointmentsAPI, analyticsAPI, usersAPI, therapiesAPI, bedsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const NAV_ITEMS = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: Activity },
  { path: '/admin/users', label: 'User Management', icon: Users },
  { path: '/admin/appointments', label: 'Appointments', icon: Calendar },
  { path: '/admin/beds', label: 'Bed Management', icon: BedDouble },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/centers', label: 'Centers', icon: Building2 },
];

const fmt = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

// ========= APPOINTMENTS =========
export const AdminAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [therapies, setTherapies] = useState([]);
  const [centers, setCenters] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState({ patient: '', doctor: '', therapy: '', center: '', date: '', startTime: '', endTime: '', type: 'therapy', notes: '' });

  const load = () => {
    setLoading(true);
    Promise.all([
      appointmentsAPI.getAll({ limit: 50 }),
      usersAPI.getAll({ role: 'patient', limit: 100 }),
      usersAPI.getDoctors(),
      therapiesAPI.getAll(),
      analyticsAPI.getCenters()
    ]).then(([apptRes, patRes, docRes, thRes, cenRes]) => {
      setAppointments(apptRes.data.appointments || []);
      setPatients(patRes.data.users || []);
      setDoctors(docRes.data.doctors || []);
      setTherapies(thRes.data.therapies || []);
      setCenters(cenRes.data.centers || []);
    }).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.patient || !form.doctor || !form.date || !form.startTime) return toast.error('Fill required fields');
    setSaving(true);
    try {
      await appointmentsAPI.create({ ...form, endTime: form.endTime || form.startTime });
      toast.success('Appointment scheduled!');
      setShowCreate(false);
      setForm({ patient: '', doctor: '', therapy: '', center: '', date: '', startTime: '', endTime: '', type: 'therapy', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to schedule'); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await appointmentsAPI.update(id, { status });
      setAppointments(prev => prev.map(a => a._id === id ? { ...a, status } : a));
      toast.success('Status updated');
    } catch { toast.error('Failed'); }
  };

  const filtered = statusFilter ? appointments.filter(a => a.status === statusFilter) : appointments;

  if (loading) return <DashboardLayout navItems={NAV_ITEMS} role="admin"><LoadingState /></DashboardLayout>;

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="admin">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-800">Appointments ({appointments.length})</h1>
        <Button onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" /> Schedule</Button>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {['', 'scheduled', 'confirmed', 'in-progress', 'completed', 'cancelled'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-all capitalize
              ${statusFilter === s ? 'bg-violet-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}>
            {s || 'All'} {s && `(${appointments.filter(a => a.status === s).length})`}
          </button>
        ))}
      </div>

      <Card>
        {filtered.length === 0 ? <EmptyState icon={Calendar} title="No appointments" /> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  {['Patient', 'Doctor', 'Date & Time', 'Therapy', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left text-xs font-bold text-gray-400 uppercase tracking-wider pb-3 pr-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(a => (
                  <tr key={a._id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <Avatar name={a.patient?.name} color="emerald" size="sm" />
                        <div>
                          <p className="text-sm font-medium text-gray-800">{a.patient?.name}</p>
                          {a.patient?.patientProfile?.dosha && <DoshaTag dosha={a.patient.patientProfile.dosha} />}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm text-gray-700">{a.doctor?.name}</p>
                      <p className="text-xs text-gray-400">{a.doctor?.doctorProfile?.specialization}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm text-gray-700">{fmt(a.date)}</p>
                      <p className="text-xs text-gray-400">{a.startTime} – {a.endTime}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm text-gray-700">{a.therapy?.name || '—'}</p>
                      <Badge color="gray">{a.type}</Badge>
                    </td>
                    <td className="py-3 pr-4"><StatusBadge status={a.status} /></td>
                    <td className="py-3">
                      <Select value={a.status} onChange={e => updateStatus(a._id, e.target.value)} className="text-xs py-1.5 w-32">
                        {['scheduled','confirmed','in-progress','completed','cancelled','no-show'].map(s => <option key={s} value={s}>{s}</option>)}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Schedule Appointment" size="md">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Select label="Patient *" value={form.patient} onChange={e => setForm(f => ({ ...f, patient: e.target.value }))}>
              <option value="">Select patient</option>
              {patients.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </Select>
            <Select label="Doctor *" value={form.doctor} onChange={e => setForm(f => ({ ...f, doctor: e.target.value }))}>
              <option value="">Select doctor</option>
              {doctors.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
            </Select>
            <Select label="Therapy" value={form.therapy} onChange={e => setForm(f => ({ ...f, therapy: e.target.value }))}>
              <option value="">Select therapy</option>
              {therapies.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
            </Select>
            <Select label="Center" value={form.center} onChange={e => setForm(f => ({ ...f, center: e.target.value }))}>
              <option value="">Select center</option>
              {centers.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </Select>
            <Input label="Date *" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            <Select label="Type" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
              <option value="therapy">Therapy</option>
              <option value="consultation">Consultation</option>
              <option value="follow-up">Follow-up</option>
            </Select>
            <Input label="Start Time *" type="time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} />
            <Input label="End Time" type="time" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-500 bg-gray-50 resize-none" />
          </div>
          <div className="flex gap-3 justify-end pt-2 border-t">
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} loading={saving} className="bg-violet-600 hover:bg-violet-700">Schedule</Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

// ========= ANALYTICS =========
export const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsAPI.get().then(r => setData(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, []);

  if (loading) return <DashboardLayout navItems={NAV_ITEMS} role="admin"><LoadingState /></DashboardLayout>;

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weeklyData = DAYS.map((day, i) => ({
    day, appointments: data?.charts?.weeklyAppointments?.find(w => w._id === i + 1)?.count || 0
  }));

  // Simulated monthly trend
  const monthlyTrend = Array.from({ length: 6 }, (_, i) => ({
    month: ['Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr'][i],
    patients: Math.floor(Math.random() * 30) + 20,
    appointments: Math.floor(Math.random() * 80) + 50,
    revenue: Math.floor(Math.random() * 50000) + 30000,
  }));

  const stats = data?.stats || {};

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="admin">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Analytics & Reports</h1>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Patients', value: stats.totalPatients, change: '+12%', color: 'emerald' },
          { label: 'Total Doctors', value: stats.totalDoctors, change: '+2', color: 'blue' },
          { label: 'Completion Rate', value: `${stats.monthAppointments ? Math.round((stats.completedThisMonth / stats.monthAppointments) * 100) || 0 : 0}%`, change: '+5%', color: 'violet' },
          { label: 'Bed Utilization', value: `${stats.occupancyRate || 0}%`, change: '-3%', color: 'amber' },
        ].map(kpi => (
          <div key={kpi.label} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <p className="text-gray-500 text-xs font-medium">{kpi.label}</p>
            <p className="text-2xl font-bold text-gray-800 mt-1">{kpi.value}</p>
            <p className={`text-xs mt-1 font-medium ${kpi.change.startsWith('+') ? 'text-emerald-600' : 'text-red-500'}`}>{kpi.change} vs last month</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card title="Weekly Appointments" subtitle="Session frequency this week">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={weeklyData} barSize={30}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
              <Bar dataKey="appointments" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="6-Month Trend" subtitle="Patient & appointment growth">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
              <Line type="monotone" dataKey="patients" stroke="#10b981" strokeWidth={2.5} dot={{ fill: '#10b981', r: 4 }} name="Patients" />
              <Line type="monotone" dataKey="appointments" stroke="#8b5cf6" strokeWidth={2.5} dot={{ fill: '#8b5cf6', r: 4 }} name="Appointments" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Revenue Trend */}
      <Card title="Revenue Trend (₹)" subtitle="Estimated monthly revenue from sessions">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={monthlyTrend} barSize={40}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ borderRadius: '12px', border: 'none' }} formatter={v => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
            <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
              {monthlyTrend.map((_, i) => (
                <rect key={i} fill={`hsl(${150 + i * 10}, 60%, ${50 - i * 3}%)`} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </DashboardLayout>
  );
};

// ========= CENTERS =========
export const AdminCenters = () => {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', city: '', state: '', phone: '', email: '', totalBeds: '' });

  useEffect(() => {
    analyticsAPI.getCenters().then(r => setCenters(r.data.centers || [])).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!form.name || !form.city) return toast.error('Fill required fields');
    setSaving(true);
    try {
      const res = await analyticsAPI.createCenter({ ...form, totalBeds: Number(form.totalBeds) || 0, availableBeds: Number(form.totalBeds) || 0 });
      setCenters(prev => [...prev, res.data.center]);
      setShowCreate(false);
      toast.success('Center created!');
      setForm({ name: '', address: '', city: '', state: '', phone: '', email: '', totalBeds: '' });
    } catch { toast.error('Failed to create center'); }
    finally { setSaving(false); }
  };

  if (loading) return <DashboardLayout navItems={NAV_ITEMS} role="admin"><LoadingState /></DashboardLayout>;

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="admin">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-800">Therapy Centers</h1>
        <Button onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" /> Add Center</Button>
      </div>

      {centers.length === 0 ? (
        <Card><EmptyState icon={Building2} title="No centers added" description="Add your first therapy center" action={<Button onClick={() => setShowCreate(true)}>Add Center</Button>} /></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {centers.map(c => (
            <div key={c._id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center text-white">
                  <Building2 className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-gray-800">{c.name}</h3>
                    <Badge color={c.isActive ? 'emerald' : 'red'}>{c.isActive ? 'Active' : 'Inactive'}</Badge>
                  </div>
                  <p className="text-gray-500 text-sm mt-1">{c.address}, {c.city}, {c.state}</p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
                    {c.phone && <p>📞 {c.phone}</p>}
                    {c.email && <p>✉️ {c.email}</p>}
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <div className="bg-emerald-50 rounded-xl px-3 py-1.5 text-center">
                      <p className="text-xs text-gray-400">Total Beds</p>
                      <p className="font-bold text-emerald-600">{c.totalBeds}</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl px-3 py-1.5 text-center">
                      <p className="text-xs text-gray-400">Available</p>
                      <p className="font-bold text-blue-600">{c.availableBeds}</p>
                    </div>
                  </div>
                  {c.facilities?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {c.facilities.map(f => <Badge key={f} color="gray">{f}</Badge>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add New Center" size="md">
        <div className="space-y-4">
          <Input label="Center Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., Avartana Care — South Branch" />
          <Input label="Address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Street address" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="City *" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="City" />
            <Input label="State" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} placeholder="State" />
            <Input label="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91-XXXXXXXXXX" />
            <Input label="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="center@email.com" />
            <Input label="Total Beds" type="number" value={form.totalBeds} onChange={e => setForm(f => ({ ...f, totalBeds: e.target.value }))} placeholder="20" />
          </div>
          <div className="flex gap-3 justify-end pt-2 border-t">
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} loading={saving} className="bg-violet-600 hover:bg-violet-700">Create Center</Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default AdminAppointments;
